import { createQueryKey } from '../query/createQueryKeyFactory';
import { QueryKey, QueryKeyParam } from '../query/types';

const payoutsKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys, 'payouts'] as const,
    id: <U extends QueryKeyParam>(payoutId: U) =>
      createQueryKey({
        all: [...keys, 'payouts', payoutId] as const,
        detail: [...keys, 'payouts', payoutId, 'detail'] as const,
      }),
  });

const payoutItemKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys, 'payoutItems'] as const,
    export: [...keys, 'payoutItems', 'export'] as const,
    id: <U extends QueryKeyParam>(payoutItemId: U) =>
      createQueryKey({
        all: [...keys, 'payoutItems', payoutItemId] as const,
        detail: [...keys, 'payoutItems', payoutItemId, 'detail'] as const,
      }),
  });

const payoutSummaryKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys, 'payoutSummaryKeys'] as const,
    accountCodes: [...keys, 'payoutSummaryKeys', 'accountCodes', 'export'] as const,
    accountCodesExport: [...keys, 'payoutSummaryKeys', 'accountCodes'] as const,
    storeItems: [...keys, 'payoutSummaryKeys', 'storeItems'] as const,
    storeItemsExport: [...keys, 'payoutSummaryKeys', 'storeItems', 'export'] as const,
  });

const storeItemKeys = <T extends QueryKey>(keys: T) =>
  createQueryKey({
    all: [...keys, 'storeItems'] as const,
    id: <U extends QueryKeyParam>(storeItemId: U) =>
      createQueryKey({
        all: [...keys, 'storeItems', storeItemId] as const,
        count: [...keys, 'storeItems', storeItemId, 'count'] as const,
      }),
  });

const payoutIdKey = <T extends QueryKey, U extends QueryKeyParam>(keys: T, causeId: U) =>
  createQueryKey({
    all: [...keys, causeId] as const,
    detail: [...keys, causeId, 'detail'] as const,
    payouts: payoutsKeys([...keys, causeId] as const),
    payoutItems: payoutItemKeys([...keys, causeId] as const),
    payoutSummary: payoutSummaryKeys([...keys, causeId] as const),
    storeItems: storeItemKeys([...keys, causeId] as const),
  });

const hierarchyKeys = <T extends QueryKey, U extends QueryKeyParam>(keys: T, causeId: U) =>
  createQueryKey({
    all: [...keys, 'hierarchy', causeId] as const,
  });

const payoutKeys = createQueryKey({
  all: ['payout'] as const,
  id: <T extends QueryKeyParam>(id: T) => payoutIdKey(['payout'] as const, id),
  hierarchy: <T extends QueryKeyParam>(id: T) => hierarchyKeys(['payout'] as const, id),
});

export { payoutKeys };
